import React, { useState, useEffect, useRef } from 'react';

/**
 * Volltext-Suche in der Sidebar (über alle Chats + Messages).
 * - Debounced (250ms)
 * - Treffer-Cards mit <mark>-Highlights aus FTS5-Snippets
 * - Click → Chat öffnen
 */

// Das FTS5-Snippet enthält den ROHEN Nachrichtentext (User + LLM), nur Treffer sind
// in <mark> gewrappt. Vor dem Rendern via dangerouslySetInnerHTML ALLES escapen und
// danach NUR die <mark>-Tags wiederherstellen — sonst kann aktiver HTML-Inhalt aus
// einer Nachricht (z.B. ein LLM-echotes <img onerror=...>) ausgeführt werden (XSS).
function safeSnippet(s) {
  const esc = (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return esc
    .replace(/&lt;mark&gt;/g, '<mark>')
    .replace(/&lt;\/mark&gt;/g, '</mark>');
}
export default function SearchBar({ onSelectChat, authToken }) {
  const [q, setQ]               = useState('');
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [open, setOpen]         = useState(false);
  const debounceRef             = useRef(null);
  const containerRef            = useRef(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/chats/search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) throw new Error(`${r.status}`);
        const d = await r.json();
        setResults(d.results || []);
      } catch (e) {
        setError(e.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, authToken]);

  // Click-outside closes dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSelect = (chatId) => {
    onSelectChat(chatId);
    setQ('');
    setOpen(false);
  };

  return (
    <div className="searchbar" ref={containerRef}>
      <div className="searchbar-input-wrap">
        <span className="searchbar-icon">⌕</span>
        <input
          type="search"
          className="searchbar-input"
          placeholder="Chats durchsuchen…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
        />
        {q && (
          <button className="searchbar-clear" onClick={() => setQ('')} title="Leeren">✕</button>
        )}
      </div>

      {open && (
        <div className="searchbar-dropdown">
          {loading && <div className="searchbar-state">Suche…</div>}
          {error && <div className="searchbar-state searchbar-error">Fehler: {error}</div>}
          {!loading && !error && results.length === 0 && q && (
            <div className="searchbar-state">Keine Treffer für „{q}"</div>
          )}
          {results.map(r => (
            <button
              key={r.chat_id}
              className="searchbar-result"
              onClick={() => handleSelect(r.chat_id)}
              title={`Chat öffnen: ${r.chat_title}`}
              aria-label={`Chat öffnen: ${r.chat_title}`}
            >
              <div className="searchbar-result-title">{r.chat_title}</div>
              {r.matches.slice(0, 2).map(m => (
                <div
                  key={m.msg_id}
                  className="searchbar-result-snippet"
                  // Das FTS5-Snippet enthält den ROHEN Nachrichtentext (User+LLM),
                  // nur Treffer sind in <mark> gewrappt → vor dem Rendern alles
                  // escapen und NUR die <mark>-Tags wiederherstellen (XSS-Schutz).
                  dangerouslySetInnerHTML={{ __html: safeSnippet(m.snippet) }}
                />
              ))}
              {r.matches.length > 2 && (
                <div className="searchbar-result-more">+{r.matches.length - 2} weitere Treffer</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
